<template>
  <div class="devolucoes-page">
    <nav class="page-breadcrumb mb-1">
      <router-link :to="{ name: 'dashboard-ptc' }" class="breadcrumb-link">
        <feather-icon icon="HomeIcon" size="14" />
        <span>Operações</span>
      </router-link>
      <feather-icon icon="ChevronRightIcon" size="14" class="breadcrumb-separator mx-25" />
      <span class="breadcrumb-current">Devoluções e Trocas</span>
    </nav>

    <!-- KPI row -->
    <b-row class="mb-1">
      <b-col v-for="kpi in kpis" :key="kpi.status" md="3" sm="6" class="mb-1">
        <b-card
          class="kpi-card mb-0"
          :class="{ 'kpi-card--active': filters.status === kpi.status }"
          style="cursor:pointer"
          @click="toggleStatusFilter(kpi.status)"
        >
          <div class="d-flex align-items-center">
            <b-avatar :variant="kpi.variant" size="42" class="mr-1">
              <feather-icon :icon="kpi.icon" size="18" />
            </b-avatar>
            <div>
              <div class="font-weight-bold h4 mb-0">{{ kpi.count }}</div>
              <small class="text-muted">{{ kpi.label }}</small>
            </div>
          </div>
        </b-card>
      </b-col>
    </b-row>

    <!-- Filters + actions -->
    <b-card class="mb-1">
      <b-row class="align-items-end">
        <b-col md="2" sm="6">
          <label class="small text-muted">Status</label>
          <b-form-select v-model="filters.status" size="sm" @change="buscar">
            <b-form-select-option :value="null">Todos</b-form-select-option>
            <b-form-select-option v-for="s in statusOptions" :key="s.value" :value="s.value">{{ s.label }}</b-form-select-option>
          </b-form-select>
        </b-col>
        <b-col md="2" sm="6">
          <label class="small text-muted">Tipo</label>
          <b-form-select v-model="filters.tipo" size="sm" @change="buscar">
            <b-form-select-option :value="null">Todos</b-form-select-option>
            <b-form-select-option value="DEVOLUCAO">Devolução</b-form-select-option>
            <b-form-select-option value="TROCA">Troca</b-form-select-option>
          </b-form-select>
        </b-col>
        <b-col md="2" sm="6">
          <label class="small text-muted">Data início</label>
          <b-form-input v-model="filters.dataInicial" type="date" size="sm" @change="buscar" />
        </b-col>
        <b-col md="2" sm="6">
          <label class="small text-muted">Data fim</label>
          <b-form-input v-model="filters.dataFinal" type="date" size="sm" @change="buscar" />
        </b-col>
        <b-col md="2" sm="6">
          <label class="small text-muted">Cliente (ID)</label>
          <b-form-input v-model="filters.clienteid" size="sm" placeholder="Ex: 12345" @keydown.enter="buscar" />
        </b-col>
        <b-col md="2" sm="6" class="d-flex align-items-end" style="gap:6px">
          <b-button size="sm" variant="primary" @click="buscar" :disabled="isLoading">
            <feather-icon icon="SearchIcon" size="13" />
          </b-button>
          <b-button size="sm" variant="outline-secondary" @click="limparFiltros">
            <feather-icon icon="XIcon" size="13" />
          </b-button>
          <b-button size="sm" variant="relief-success" class="ml-auto" @click="abrirNova">
            <feather-icon icon="PlusIcon" size="13" class="mr-25" />Nova
          </b-button>
        </b-col>
      </b-row>
    </b-card>

    <!-- Table -->
    <b-card>
      <div v-if="isLoading" class="text-center py-4">
        <b-spinner />
      </div>
      <div v-else-if="!devolucoes.length" class="text-center py-4 text-muted">
        <feather-icon icon="InboxIcon" size="36" class="mb-1" /><br>
        Nenhuma solicitação encontrada.
      </div>
      <b-table
        v-else
        :items="devolucoes"
        :fields="fields"
        hover
        responsive
        small
        @row-clicked="verDetalhes"
        style="cursor:pointer"
      >
        <template #cell(devolucaoid)="{ item }">
          <strong>#{{ item.devolucaoid }}</strong>
        </template>
        <template #cell(tipo)="{ item }">
          <b-badge :variant="item.tipo === 'TROCA' ? 'light-warning' : 'light-info'">
            {{ item.tipo === 'TROCA' ? 'Troca' : 'Devolução' }}
          </b-badge>
        </template>
        <template #cell(status)="{ item }">
          <b-badge :variant="statusVariant(item.status)">{{ statusLabel(item.status) }}</b-badge>
        </template>
        <template #cell(cliente_nome)="{ item }">
          <span>{{ item.cliente_nome || item.clienteid }}</span>
        </template>
        <template #cell(created_at)="{ item }">
          {{ formatDate(item.created_at) }}
        </template>
        <template #cell(acoes)="{ item }">
          <b-button size="sm" variant="flat-primary" @click.stop="verDetalhes(item)">
            <feather-icon icon="EyeIcon" size="13" />
          </b-button>
        </template>
      </b-table>

      <div v-if="meta.totalPages > 1" class="d-flex justify-content-end mt-1">
        <b-pagination
          v-model="currentPage"
          :total-rows="meta.total"
          :per-page="meta.pageSize"
          size="sm"
          @change="onPageChange"
        />
      </div>
    </b-card>

    <NovaDevolucao ref="modalNova" @criado="onCriado" />
  </div>
</template>

<script>
import { mapGetters, mapActions } from 'vuex';
import NovaDevolucao from './modal/NovaDevolucao.vue';

const STATUS_MAP = {
  AGUARDANDO:       { label: 'Aguardando',       variant: 'warning' },
  EM_ANALISE:       { label: 'Em Análise',        variant: 'info' },
  APROVADO:         { label: 'Aprovado',          variant: 'primary' },
  REPROVADO:        { label: 'Reprovado',         variant: 'danger' },
  COLETA_AGENDADA:  { label: 'Coleta Agendada',   variant: 'primary' },
  COLETADO:         { label: 'Coletado',          variant: 'primary' },
  RECEBIDO:         { label: 'Recebido',          variant: 'primary' },
  CONCLUIDO:        { label: 'Concluído',         variant: 'success' },
  CANCELADO:        { label: 'Cancelado',         variant: 'secondary' },
};

export default {
  name: 'DevolucoesTrocas',
  components: { NovaDevolucao },
  data() {
    return {
      filters: { status: null, tipo: null, dataInicial: '', dataFinal: '', clienteid: '' },
      currentPage: 1,
      fields: [
        { key: 'devolucaoid', label: 'Nº',         sortable: false },
        { key: 'tipo',        label: 'Tipo',        sortable: false },
        { key: 'status',      label: 'Status',      sortable: false },
        { key: 'cliente_nome',label: 'Cliente',     sortable: false },
        { key: 'total_itens', label: 'Itens',       sortable: false },
        { key: 'created_at',  label: 'Data',        sortable: false },
        { key: 'acoes',       label: '',            sortable: false },
      ],
      statusOptions: Object.entries(STATUS_MAP).map(([value, { label }]) => ({ value, label })),
    };
  },
  computed: {
    ...mapGetters('Devolucoes', ['isLoading', 'devolucoes', 'meta']),
    kpis() {
      const counts = {};
      this.devolucoes.forEach((d) => { counts[d.status] = (counts[d.status] || 0) + 1; });
      return [
        { status: 'AGUARDANDO',   label: 'Aguardando',   icon: 'ClockIcon',       variant: 'warning', count: counts.AGUARDANDO || 0 },
        { status: 'EM_ANALISE',   label: 'Em Análise',   icon: 'SearchIcon',      variant: 'info',    count: counts.EM_ANALISE || 0 },
        { status: 'APROVADO',     label: 'Aprovados',    icon: 'CheckCircleIcon', variant: 'primary', count: counts.APROVADO || 0 },
        { status: 'CONCLUIDO',    label: 'Concluídos',   icon: 'PackageIcon',     variant: 'success', count: counts.CONCLUIDO || 0 },
      ];
    },
  },
  created() {
    this.buscar();
  },
  methods: {
    ...mapActions('Devolucoes', ['listar', 'setFilters', 'clearFilters']),
    async buscar() {
      await this.setFilters({ ...this.filters, page: this.currentPage });
      await this.listar();
    },
    async limparFiltros() {
      this.filters = { status: null, tipo: null, dataInicial: '', dataFinal: '', clienteid: '' };
      this.currentPage = 1;
      await this.clearFilters();
      await this.listar();
    },
    toggleStatusFilter(status) {
      this.filters.status = this.filters.status === status ? null : status;
      this.buscar();
    },
    onPageChange(page) {
      this.currentPage = page;
      this.buscar();
    },
    abrirNova() {
      this.$refs.modalNova.abrir();
    },
    verDetalhes(item) {
      this.$router.push({ name: 'devolucoes-detalhes', params: { id: item.devolucaoid } });
    },
    onCriado() {
      this.buscar();
    },
    statusLabel(s)   { return STATUS_MAP[s]?.label   || s; },
    statusVariant(s) { return STATUS_MAP[s]?.variant  || 'secondary'; },
    formatDate(d) {
      if (!d) return '—';
      return new Date(d).toLocaleDateString('pt-BR');
    },
  },
};
</script>

<style scoped>
.kpi-card { transition: box-shadow .15s, border-color .15s; }
.kpi-card:hover { box-shadow: 0 4px 16px rgba(0,0,0,.12); }
.kpi-card--active { border: 2px solid var(--primary) !important; }
</style>
